import {DateTime} from 'luxon';

import {AppRoutesPrefixesEnum} from '../../../components/appRoutesPrefixes';
import {AppState} from '../../appState';
import {
  AnalyticsParameters,
  AnalyticsParametersNamespacesEnum,
} from '../analytics/parameters/analyticsParameters';
import {PanelRoute} from '../panelRoutes';
import {ALL_DATA_ID} from './views/analyticsPanelViewsState';

/*
 * Types.
 */

export type AnalyticsPanelRoute = PanelRoute<AppState, AnalyticsParameters | undefined>;

/*
 * Routes.
 */

// Routes are matched from top to bottom. So the more specific routes need to be at the top.
export function buildAnalyticsPanelRoutes(): ReadonlyArray<AnalyticsPanelRoute> {
  const {defaultStartTime, defaultEndTime} = getDefaultDates();

  return [
    {
      path: `/team/:teamId/:reportType`,
      getParameters: (_state, {params, search}) => {
        const {teamId, reportType} = params;
        const {from, to, selectedViewIdFormatted} = getParamsFromSearch(search);

        return {
          location: {
            pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/team/${teamId}/${reportType}`,
            search,
          },
          namespace: AnalyticsParametersNamespacesEnum.TEAM_REPORT,
          teamId,
          reportType,
          query: {
            from: from ? Number(from) : defaultStartTime,
            to: to ? Number(to) : defaultEndTime,
            selectedViewId: selectedViewIdFormatted,
          },
        };
      },
    },
    {
      path: `/teammate/:teammateId/:reportType`,
      getParameters: (_state, {params, search}) => {
        const {teammateId, reportType} = params;
        const {from, to, selectedViewIdFormatted} = getParamsFromSearch(search);

        return {
          location: {
            pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/teammate/${teammateId}/${reportType}`,
            search,
          },
          namespace: AnalyticsParametersNamespacesEnum.TEAMMATE_REPORT,
          teammateId,
          reportType,
          query: {
            from: from ? Number(from) : defaultStartTime,
            to: to ? Number(to) : defaultEndTime,
            selectedViewId: selectedViewIdFormatted,
          },
        };
      },
    },
    {
      path: `/global/:reportType`,
      getParameters: (_state, {params, search}) => {
        const {reportType} = params;
        const {from, to, selectedViewIdFormatted} = getParamsFromSearch(search);

        return {
          location: {
            pathname: `/${AppRoutesPrefixesEnum.ANALYTICS}/global/${reportType}`,
            search,
          },
          namespace: AnalyticsParametersNamespacesEnum.GLOBAL,
          reportType,
          query: {
            from: from ? Number(from) : defaultStartTime,
            to: to ? Number(to) : defaultEndTime,
            selectedViewId: selectedViewIdFormatted,
          },
        };
      },
    },
  ];
}

function getDefaultDates() {
  // Default time period is last rolling 7 days (ending yesterday 23:59:59.999).
  const todayStartOfDay = DateTime.local();
  const defaultStartTime = todayStartOfDay.minus({day: 7}).startOf('day').toMillis();
  const defaultEndTime = todayStartOfDay.minus({day: 1}).endOf('day').toMillis();

  return {defaultStartTime, defaultEndTime};
}

function getParamsFromSearch(search: string) {
  const urlSearchParams = new URLSearchParams(search);
  const from = urlSearchParams.get('from');
  const to = urlSearchParams.get('to');
  const selectedViewId = urlSearchParams.get('selectedViewId') ?? undefined;
  // SelectedViewId can be string or number or undefined
  const selectedViewIdFormatted =
    selectedViewId && selectedViewId === ALL_DATA_ID
      ? selectedViewId
      : selectedViewId && (Number(selectedViewId) || selectedViewId);

  return {from, to, selectedViewIdFormatted};
}
